import { CircularProgress, createStyles, makeStyles } from '@material-ui/core';
import { NftInclude } from '@mogul/api-client';
import { getOpenseaUrl } from '@mogul/shared';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import * as r from 'runtypes';

import { getNftIfMinted } from '../../../api-helpers/nft';
import { ReactComponent as BlackStarIcon } from '../../../common/svg/button-icons/black_star_icon.svg';
import { BackHeader } from '../../../components/common/backheader/BackHeader.component';
import { CollectRewardsSingleRoundModal } from '../../../components/common/modal/movie-voting/CollectRewardsSingleRoundModal.component';
import type { VoteModalProps } from '../../../components/common/modal/movie-voting/VoteModal.component';
import { VoteModal } from '../../../components/common/modal/movie-voting/VoteModal.component';
import {
  Button,
  Colors,
} from '../../../components/legacy/button/Button.component';
import type { CastAndCrewData } from '../../../components/movie/card/AdditionalInfo.component';
import { NotificationCard } from '../../../components/movie-voting/NotificationCard.component';
import { ProjectVoting } from '../../../components/movie-voting/ProjectVoting.component';
import { AVERAGE_BLOCK_TIME_BSC } from '../../../constants/voting';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useNetwork, useSwitchChain, useWeb3State } from '../../../hooks';
import { useMovieVotingContract } from '../../../hooks/movie-voting.hooks';
import type { MovieRound } from '../../../hooks/voting.hooks';
import {
  useVotingContract,
  VotingRoundState,
} from '../../../hooks/voting.hooks';
import { CHAIN_ID_TO_TYPE_MAP, WEB3_CONFIG } from '../../../lib';
import type { ProjectMetadata } from '../project/ProjectPage.container';
import styles from './PreviousRoundPage.module.css';

const useStyles = makeStyles(() =>
  createStyles({
    toolTips: {
      maxWidth: '40ch',
      background: '#000000',
      marginLeft: '10px',
    },
    loader: { color: 'white' },
  }),
);

const INITIAL_MOVIE_ROUND: MovieRound = {
  roundId: '',
  movieIds: [],
  votes: [],
  startBlock: '0',
  endBlock: '0',
  starsRewards: '0',
  votingState: VotingRoundState.Canceled,
};

export const PreviousRoundPage: React.FC = () => {
  const { setModal } = useContext(ModalStateContext);
  const history = useHistory();
  const classes = useStyles();
  const { walletDetails } = useWeb3State();
  const { switchNetworkAsync } = useSwitchChain();
  const { isUnsupported } = useNetwork();

  const { roundId, placeInPastRounds } = r
    .Record({ roundId: r.String })
    .And(r.Partial({ placeInPastRounds: r.String }))
    .check(useParams());

  const handleVoteOnClick = (props: VoteModalProps) => {
    setModal(VoteModal(props));
  };

  const movieVotingContract = useMovieVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.eth.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.eth.id],
    hardOverride: false,
  });

  const votingContract = useVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.bsc.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.bsc.id],
    hardOverride: false,
  });

  const {
    initialized,
    getVotingRoundInfo,
    getUserStarsForMovie,
    claimRewardsForRound,
    calculateStarsRewards,
    getBlockTimestamp,
    getCurrentBlock,
  } =
    walletDetails.chain.id === WEB3_CONFIG.chains.eth.id
      ? movieVotingContract
      : votingContract;

  const [loading, setLoading] = useState(true);
  const [movieRound, setMovieRound] = useState(INITIAL_MOVIE_ROUND);
  const [userStars, setUserStars] = useState<string[]>([]);
  const [userVotedState, setUserVotedState] = useState<Record<string, string>>(
    {},
  );
  const [NFTMetaData, setNFTMetaData] = useState<
    Record<string, ProjectMetadata>
  >({});

  const [cast, setCast] = useState<Record<string, CastAndCrewData[]>>({});
  const [userStarsReward, setUserStarsReward] = useState<string>('');

  const [startDate, setStartDate] = useState(new Date());
  const [estimatedStartDate, setEstimatedStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    const setupMovieVoting = async () => {
      const round = await getVotingRoundInfo(roundId);

      setMovieRound(round);
      for (const movieId of round.movieIds) {
        const nft = await getNftIfMinted(
          WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingNft,
          movieId,
          [NftInclude.Project],
        );
        const movieData = nft?.project;

        setNFTMetaData(prev => ({
          ...prev,
          [movieId]: {
            description: movieData?.description ?? '',
            genre: movieData?.genre ?? '',
            image: movieData?.posterImage?.publicUrl ?? '',
            name: movieData?.title ?? '',
            studio: movieData?.studio ?? '',
          } as ProjectMetadata,
        }));

        const crewData = (movieData?.crew ?? []) as CastAndCrewData[];
        const castData = (movieData?.cast ?? []) as CastAndCrewData[];
        const castAndCrewData: CastAndCrewData[] = crewData.concat(
          castData.map(castObj => ({ ...castObj, role: 'Actor' })),
        );

        setCast(prev => ({ ...prev, [movieId]: castAndCrewData }));

        const userVotedResult = await getUserStarsForMovie(roundId, movieId);
        setUserStars(prev => [...prev, userVotedResult]);
        setUserVotedState(prev => ({
          ...prev,
          [movieId]: Math.round(
            Math.sqrt(parseFloat(userVotedResult)),
          ).toString(),
        }));
      }

      const reward = await calculateStarsRewards(roundId);
      const startDateBlock = await getBlockTimestamp(
        parseInt(round.startBlock, 10),
      );
      const endDateBlock = await getBlockTimestamp(
        parseInt(round.endBlock, 10),
      );

      if (startDateBlock === 0) {
        const currentBlock = await getCurrentBlock();
        // 13s for average block time and 1000 to get milliseconds
        setEstimatedStartDate(
          new Date(
            Date.now() +
              (parseInt(round.startBlock, 10) - currentBlock.number) *
                AVERAGE_BLOCK_TIME_BSC *
                1000,
          ),
        );
      }

      setUserStarsReward(reward);
      setStartDate(new Date(startDateBlock * 1000));
      setEndDate(new Date(endDateBlock * 1000));
      setLoading(false);
    };

    setLoading(true);
    if (initialized) {
      void setupMovieVoting();
    }
  }, [initialized, walletDetails]);

  const openseaLink = getOpenseaUrl(
    WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingNft,
    '',
  );
  const handleClaimRewards = async (previousRoundId: string) => {
    const result = await claimRewardsForRound(previousRoundId);
    return result;
  };

  const collectRewards = () =>
    placeInPastRounds !== undefined
      ? setModal(
          CollectRewardsSingleRoundModal(
            movieRound,
            userStars,
            userStarsReward,
            handleClaimRewards,
            placeInPastRounds,
          ),
        )
      : setModal(
          CollectRewardsSingleRoundModal(
            movieRound,
            userStars,
            userStarsReward,
            handleClaimRewards,
          ),
        );

  const handleViewOnClick = (movieId: string) => {
    history.push(`/platform/voting/${roundId}/nft/${movieId}`);
  };

  const headerOnPress = () => {
    history.push('/platform/voting');
  };

  const handleBnbSwitch = useCallback(
    () => switchNetworkAsync?.(WEB3_CONFIG.chains.bsc.id),
    [switchNetworkAsync],
  );

  if (isUnsupported) {
    return (
      <div
        className={
          styles.movieVotingPage + ' flex flex-col items-center justify-center'
        }
      >
        <div className={styles.movieVotingContainer + ' max-w-screen-lg mt-10'}>
          <div className="flex flex-col items-center justify-center w-full h-full">
            <div className="flex flex-col items-center justify-center h-full max-w-screen-lg w-4/5">
              <p className="text-lg">You are using the wrong network ...</p>
              <Button className="mt-8 py-3" onClick={handleBnbSwitch}>
                Switch To BNB Chain
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (movieRound.movieIds.length === 0 && !loading) {
    return (
      <div className={styles.movieVotingPage}>
        <div className={styles.movieVotingContainer + ' max-w-screen-lg mt-10'}>
          <div className={'w-full flex justify-between'}>
            <div>
              <div
                className={
                  styles.voting +
                  ' 2xl:text-7xl xl:text-6xl lg:text-5xl text-4xl'
                }
              >
                PROJECT
              </div>
            </div>
          </div>
          <div className="py-10">
            <div className={' w-full lg:w-full pb-3 text-base xl:text-lg'}>
              The Mogul voting contract uses a quadratic formula to calculate
              how many votes each STARS holder gets when voting on which project
              Mogul will finance.
            </div>
            <div className={' w-full lg:w-full pb-3 text-base xl:text-lg'}>
              The number of votes a STARS holder receives is equal to the square
              root of the number of STARS they have.
            </div>
            <div className={' w-full lg:w-full text-base xl:text-lg'}>
              For more on how quadratic voting works, visit Mogul Production's{' '}
              <a
                className="underline"
                href="https://mogulproductions.gitbook.io/mogul-productions/product-information/voting"
              >
                Gitbook
              </a>
              .
            </div>
          </div>
          <div
            className={' w-full lg:w-full pb-3 text-base xl:text-lg'}
            style={{ fontWeight: 'bold' }}
          >
            There are currently no active voting rounds, please check back
            later.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-full grid place-items-center">
        <CircularProgress className={classes.loader} size={100} />
      </div>
    );
  }

  const disableCollectRewards = parseFloat(userStarsReward) > 0;

  return (
    <div className={styles.movieVotingPage}>
      <div className="w-full ml-16 mt-20 sm:mt-0">
        <BackHeader
          title="BACK TO CURRENT VOTING"
          headerOnPress={headerOnPress}
        />
      </div>
      <div className={styles.movieVotingContainer + ` max-w-screen-lg`}>
        <div className={'w-full flex justify-between'}>
          <div>
            <div
              className={
                styles.round +
                ' 2xl:text-3xl xl:text-2xl lg:text-xl md:text-lg text-lg'
              }
            >
              VOTING
            </div>
            <div
              className={
                styles.voting +
                ' 2xl:text-7xl xl:text-6xl lg:text-5xl md:text-4xl text-3xl'
              }
            >
              {startDate.getTime() === 0 ? 'FUTURE ROUND' : 'PAST ROUND'}
            </div>
          </div>
          <div>
            <div
              className={
                styles.timeLeftTitle +
                ' 2xl:text-3xl xl:text-2xl lg:text-xl md:text-lg text-lg'
              }
            >
              {startDate.getTime() === 0 ? 'VOTING STARTS ON' : 'VOTING ENDED'}
            </div>
            <div
              className={
                styles.timeLeft +
                ' 2xl:text-7xl xl:text-6xl lg:text-5xl md:text-4xl text-3xl'
              }
            >
              {VotingRoundState.Canceled !== movieRound.votingState
                ? (startDate.getTime() === 0
                    ? estimatedStartDate
                    : endDate
                  ).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                : 'CANCELLED ROUND'}
            </div>
          </div>
        </div>
        <div className="my-10">
          {disableCollectRewards && (
            <NotificationCard
              title={'Unclaimed Rewards'}
              body={'Make sure to collect all unclaimed rewards from voting.'}
              isRow={true}
            >
              <Button
                className={'flex h-12 ml-2'}
                color={Colors.COLOR_SCHEME_2}
                onClick={collectRewards}
              >
                <BlackStarIcon className="" />
                <span className="ml-2 lg:block hidden">
                  COLLECT UNCLAIMED STARS
                </span>
                <span className="ml-2 lg:hidden sm:block hidden text-sm">
                  COLLECT STARS
                </span>
              </Button>
            </NotificationCard>
          )}
        </div>
        <div className="my-10">
          <ProjectVoting
            movieIds={movieRound.movieIds}
            roundId={roundId}
            userVotes={userVotedState}
            totalVotes={movieRound.votes}
            movieMetadata={NFTMetaData}
            openseaLink={openseaLink}
            handleViewOnClick={handleViewOnClick}
            handleVoteOnClick={handleVoteOnClick}
            setUpVoting={() => {}}
            noVoteButton={true}
            // TODO: make these optional
            currentBlock={{ number: 0, timestamp: 0 }}
            startBlock={{ number: 0, timestamp: 0 }}
            endBlock={{ number: 0, timestamp: 0 }}
            getMoreStars={() => {}}
            rewardsPerBlock={0}
            numberOfVotes={'200'}
            starsSubmitted={'40001'}
            rewardGenerated={'20'}
            placeVote={(_: string) => {
              return Promise.resolve(true);
            }}
            withdrawStars={() => {}}
            cast={cast}
          />
        </div>
      </div>
    </div>
  );
};
